<script lang="ts">
    /**
     * PinPage - Pin 제품 소개 페이지 (/pin)
     *
     * 🎯 이 파일이 존재하는 이유(D9): JadEye(/jadeye)·Gridverse(/gridverse)와 **같은 층**을 채운다.
     *    이 층이 없어서 회사 홈에서 Pin 카드를 누르면 소개 없이 곧장 휴게소 목록(SSR)으로 떨어졌다.
     *    랜딩 SPA 안에 두면 앱바·푸터가 저절로 공유되므로 별도 셸을 만들 필요가 없다.
     *
     * 구조는 GridversePage 를 그대로 따른다: 히어로 + 컨셉 3장.
     * 새 서비스가 붙을 때도 이 형태를 복사하면 된다.
     *
     * 🚨 카피 원칙 — "지금 실제로 발행된 것"만 말한다. 콘텐츠 축은 현재 4개다
     *    (고속도로 휴게소 · 노포 맛집 · 타임터널 · 등산코스).
     *    아래 컨셉 카드는 전부 실제 페이지에 있는 항목이다
     *    (대표메뉴·가격 → show.blade / 노선·상하행 → route.blade / 편의시설·주차 → show.blade).
     */
    import PinHeroSection from './sections/PinHeroSection.svelte';
    import { getPin } from '../../../lib/pin';

    const pin = getPin();

    /**
     * 축별 아이콘·설명 — **목록 자체가 아니다.**
     *
     * 🚨 목록은 서버(`PinSeo::nav()` → `pin.axes`)가 정한다. 여기 표에 없는 축도 카드가 나가고
     *    (아이콘·설명만 기본값이 된다), 여기 있는데 서버가 안 주는 축은 나가지 않는다.
     *    축을 손으로 나열하던 시절 타임터널(08-18)·등산(09-08)이 발행됐는데도 이 페이지엔
     *    휴게소·노포 둘만 보였다 — 그 재발을 구조로 막는다.
     * 🚨 개수를 쓰지 않는다 — 주 1회 배치로 변해 하드코딩은 낡는다(홈 카드와 같은 결정, 2-C).
     */
    const axisMeta: Record<string, { icon: string; desc: string }> = {
        '/pin/rest-area': {
            icon: '🛣️',
            desc: '전국 휴게소의 대표메뉴·가격·편의시설을 노선별로. 메뉴별 모음과 샤워실 정보까지.',
        },
        '/pin/restaurant': {
            icon: '🍚',
            desc: '행정안전부 인허가 원장에서 찾은 오래된 식당을 지역별로. 한 곳씩 검수를 거쳤어요.',
        },
        '/pin/restaurant/time-tunnel': {
            icon: '🕰️',
            desc: '옛날 중국집·국밥집·백년식당을 업종과 연대로. 인허가 원장 기준으로 골랐어요.',
        },
        '/pin/hiking': {
            icon: '🥾',
            desc: '전국 산·국립공원의 등산코스를 지도 위 경로와 함께. 거리·표고차와 코스 위 시설까지.',
        },
    };

    /**
     * 서비스 메뉴 — /pin 이 Pin 의 출발점이므로 모든 콘텐츠 카테고리가 여기서 메뉴로 나간다
     * (2026-08-17 스위티 지적: "rest-area 와 restaurant 가 여기서부터 메뉴로 나와야").
     * 경로·라벨·개수 전부 서버 주입값(window.JadEye.pin.axes)이 SSOT — 꺼진 축은 애초에 안 온다.
     */
    const services = (pin?.axes ?? []).map((axis) => ({
        icon: axisMeta[axis.path]?.icon ?? '📍',
        title: axis.label,
        desc: axisMeta[axis.path]?.desc ?? '',
        href: axis.path,
    }));

    let conceptVisible = $state(false);

    $effect(() => {
        const t = setTimeout(() => {
            conceptVisible = true;
        }, 150);
        return () => clearTimeout(t);
    });

    const restAreaConcepts = [
        {
            icon: '🍜',
            title: '대표메뉴와 가격',
            desc: '휴게소마다 무엇을 파는지, 얼마인지 한눈에 봐요. 베스트·추천 메뉴도 함께 표시해요.',
        },
        {
            icon: '🛣️',
            title: '노선별로, 방향까지',
            desc: '경부·영동·호남… 노선을 따라 찾고 상·하행을 나눠서 봐요. 고속도로에서는 거리보다 진행 방향이 중요하니까요.',
        },
        {
            icon: '🅿️',
            title: '편의시설과 주차',
            desc: '어떤 시설이 몇 시까지 여는지, 입점 브랜드는 무엇인지, 승용차·화물차 주차면은 몇 면인지까지.',
        },
    ];

    // ⚠️ 카피 제한(키워드-후보표 §6): "창업 N년"·"가장 오래된" 금지 — "인허가 원장 기준"만 말한다.
    const oldConcepts = [
        {
            icon: '📜',
            title: '인허가 원장에서 출발',
            desc: '블로그나 입소문이 아니라 행정안전부 일반음식점 인허가 원장에서 인허가가 오래된 식당을 찾아요.',
        },
        {
            icon: '🔍',
            title: '한 곳씩 검수',
            desc: '옛 인허가만 이어받은 새 가게, 원장 입력 오류를 걸러내고 목록을 확정해요. 기준과 한계도 함께 밝혀요.',
        },
        {
            icon: '🗺️',
            title: '지역별 모음',
            desc: '지역별로 모아서 보여드려요. 연차는 인허가 원장 기준으로만 표기해요 — 창업연도와 다를 수 있으니까요.',
        },
    ];

    /**
     * 데이터를 만드는 방식 (애드센스 재심사 대응 작업 2) — **서비스 전체에 해당하는 사실만** 말한다.
     * 한 곳씩 판정하는 공정은 노포·타임터널에만 있으므로 그렇게 한정해 말한다(휴게소·등산은 자동 적재다).
     * noscript 파셜(partials/landing-noscript/pin)이 같은 문구를 옮긴다 — 고치면 둘 다 고칠 것.
     */
    const dataConcepts = [
        {
            icon: '🏛️',
            title: '공개된 원본에서 출발',
            desc: '한국도로공사·한국석유공사(오피넷)·행정안전부·국립공원공단·한국등산트레킹지원센터가 공개한 데이터를 써요. 이름·메뉴·가격은 원본 그대로 옮겨요.',
        },
        {
            icon: '🧹',
            title: '싣지 않는 기준',
            desc: '화장실·주차뿐인 졸음쉼터, 판매가 끝난 메뉴, 오래된 기름값은 싣지 않아요. 노포·타임터널은 한 곳씩 판정해 시대와 맞지 않는 곳을 걸러요.',
        },
        {
            icon: '🔄',
            title: '갱신 주기와 한계',
            desc: '휴게소·식당 정보는 매주, 휴게소 기름값은 매일 다시 받아요. 등산코스는 원본 기준일(2017·2022년) 자료라 정기 갱신이 없어요.',
        },
    ];
</script>

<!-- title·description 은 config/seo.php 가 SSOT (LandingApp.applyMeta 참고) -->

<div class="pin-page">
    <PinHeroSection />

    {#if services.length > 0}
        <!-- 서비스 메뉴 — Pin 의 모든 카테고리는 여기서 출발한다 (카테고리가 늘면 services 배열만 늘어난다) -->
        <section id="pin-services" class="services-section" aria-label="Pin 서비스">
            <div class="services-inner">
                <h2 class="services-title">무엇을 찾으세요?</h2>
                <div class="services-grid">
                    {#each services as s}
                        <a class="service-card" href={s.href}>
                            <span class="service-emoji" aria-hidden="true">{s.icon}</span>
                            <span class="service-body">
                                <span class="service-card-title">{s.title}</span>
                                <span class="service-card-desc">{s.desc}</span>
                            </span>
                            <svg class="service-arrow" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
                            </svg>
                        </a>
                    {/each}
                </div>
            </div>
        </section>
    {/if}

    <!-- 하위 서비스 ① 고속도로 휴게소 -->
    <section id="pin-concept" class="concept-section">
        <div class="concept-inner" class:visible={conceptVisible}>
            <div class="concept-head">
                <h2 class="concept-title">고속도로 휴게소</h2>
                <p class="concept-subtitle">다음 휴게소에서 뭘 먹을지, 출발 전에 정하세요</p>
            </div>

            <div class="concept-grid">
                {#each restAreaConcepts as c}
                    <div class="concept-card">
                        <span class="concept-emoji">{c.icon}</span>
                        <h3 class="concept-card-title">{c.title}</h3>
                        <p class="concept-card-desc">{c.desc}</p>
                    </div>
                {/each}
            </div>

            {#if pin}
                <div class="concept-cta">
                    <a href={pin.hub} class="concept-cta-link">
                        전국 고속도로 휴게소 보기
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                            <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
                        </svg>
                    </a>
                </div>
            {/if}
        </div>
    </section>

    <!-- 하위 서비스 ② 노포 맛집 — 휴게소와 같은 급의 블록(하위 메뉴 2개가 대등하게 보여야 한다, 2026-08-17 스위티) -->
    {#if pin?.oldHub}
        <section id="pin-old" class="concept-section concept-section-alt">
            <div class="concept-inner" class:visible={conceptVisible}>
                <div class="concept-head">
                    <h2 class="concept-title">노포 맛집</h2>
                    <p class="concept-subtitle">오래 자리를 지킨 식당을, 기록으로 찾아요</p>
                </div>

                <div class="concept-grid">
                    {#each oldConcepts as c}
                        <div class="concept-card">
                            <span class="concept-emoji">{c.icon}</span>
                            <h3 class="concept-card-title">{c.title}</h3>
                            <p class="concept-card-desc">{c.desc}</p>
                        </div>
                    {/each}
                </div>

                <div class="concept-cta">
                    <a href={pin.oldHub} class="concept-cta-link">
                        지역별 노포 맛집 보기
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                            <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
                        </svg>
                    </a>
                </div>
            </div>
        </section>
    {/if}

    <!-- 데이터를 만드는 방식 — 출처·거르는 기준·갱신 주기와 한계. 한계의 전문은 데이터 출처 문서가 말한다. -->
    {#if pin}
        <section id="pin-data" class="concept-section">
            <div class="concept-inner" class:visible={conceptVisible}>
                <div class="concept-head">
                    <h2 class="concept-title">Pin 의 정보는 이렇게 만들어요</h2>
                    <p class="concept-subtitle">어디서 가져와 무엇을 거르고, 무엇을 보장하지 않는지까지</p>
                </div>

                <div class="concept-grid">
                    {#each dataConcepts as c}
                        <div class="concept-card">
                            <span class="concept-emoji">{c.icon}</span>
                            <h3 class="concept-card-title">{c.title}</h3>
                            <p class="concept-card-desc">{c.desc}</p>
                        </div>
                    {/each}
                </div>

                {#if pin.dataSources}
                    <div class="concept-cta">
                        <a href={pin.dataSources} class="concept-cta-link">
                            데이터 출처와 한계 보기
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
                            </svg>
                        </a>
                    </div>
                {/if}
            </div>
        </section>
    {/if}
</div>

<style>
    .pin-page {
        width: 100%;
    }

    .services-section {
        padding: 3.5rem 1.5rem 0;
        background: var(--landing-bg, #ffffff);
    }

    .services-inner {
        max-width: 60rem;
        margin: 0 auto;
    }

    .services-title {
        font-size: clamp(1.4rem, 3vw, 1.8rem);
        font-weight: 700;
        letter-spacing: -0.02em;
        color: var(--landing-text, #1a1a1a);
        text-align: center;
        margin: 0 0 1.75rem;
    }

    .services-grid {
        display: grid;
        grid-template-columns: 1fr;
        gap: 1rem;
    }

    .service-card {
        display: flex;
        align-items: center;
        gap: 1.1rem;
        padding: 1.5rem 1.75rem;
        background: var(--landing-bg-alt, #f8f9fa);
        border: 1px solid rgba(46, 111, 158, 0.18);
        border-radius: 18px;
        text-decoration: none;
        transition:
            transform 0.25s cubic-bezier(0.16, 1, 0.3, 1),
            border-color 0.2s ease,
            box-shadow 0.25s ease;
    }

    .service-card:hover {
        transform: translateY(-3px);
        border-color: rgba(46, 111, 158, 0.45);
        box-shadow: 0 10px 26px rgba(46, 111, 158, 0.12);
    }

    .service-emoji {
        font-size: 2rem;
        line-height: 1;
        flex-shrink: 0;
    }

    .service-body {
        display: flex;
        flex-direction: column;
        gap: 0.3rem;
        min-width: 0;
    }

    .service-card-title {
        font-size: 1.12rem;
        font-weight: 700;
        color: var(--landing-text, #1a1a1a);
    }

    .service-card-desc {
        font-size: 0.9rem;
        line-height: 1.55;
        color: var(--landing-text-sub, #666666);
    }

    .service-arrow {
        margin-left: auto;
        flex-shrink: 0;
        color: #2e6f9e;
    }

    .concept-section {
        padding: 5rem 1.5rem;
        background: var(--landing-bg, #ffffff);
    }

    /* 두 번째 하위 서비스 블록 — 배경 톤만 살짝 갈라 경계를 준다 */
    .concept-section-alt {
        background: var(--landing-bg-alt, #f8f9fa);
    }

    .concept-section-alt .concept-card {
        background: var(--landing-bg, #ffffff);
    }

    .concept-inner {
        max-width: 72rem;
        margin: 0 auto;
        opacity: 0;
        transform: translateY(20px);
        transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
    }

    .concept-inner.visible {
        opacity: 1;
        transform: translateY(0);
    }

    .concept-head {
        text-align: center;
        margin-bottom: 3rem;
    }

    .concept-title {
        font-size: clamp(1.8rem, 4vw, 2.5rem);
        font-weight: 700;
        letter-spacing: -0.02em;
        color: var(--landing-text, #1a1a1a);
        margin: 0 0 0.75rem;
    }

    .concept-subtitle {
        font-size: 1.05rem;
        color: var(--landing-text-sub, #666666);
        margin: 0;
    }

    .concept-grid {
        display: grid;
        grid-template-columns: 1fr;
        gap: 1.5rem;
        max-width: 60rem;
        margin: 0 auto;
    }

    .concept-card {
        background: var(--landing-bg-alt, #f8f9fa);
        border: 1px solid rgba(0, 0, 0, 0.05);
        border-radius: 20px;
        padding: 2rem;
        text-align: center;
        transition:
            transform 0.3s cubic-bezier(0.16, 1, 0.3, 1),
            box-shadow 0.3s ease;
    }

    .concept-card:hover {
        transform: translateY(-4px);
        box-shadow: 0 12px 30px rgba(0, 0, 0, 0.07);
    }

    .concept-emoji {
        display: inline-block;
        font-size: 2.5rem;
        line-height: 1;
        margin-bottom: 1rem;
    }

    .concept-card-title {
        font-size: 1.2rem;
        font-weight: 700;
        color: var(--landing-text, #1a1a1a);
        margin: 0 0 0.6rem;
    }

    .concept-card-desc {
        font-size: 0.95rem;
        line-height: 1.7;
        color: var(--landing-text-sub, #666666);
        margin: 0;
    }

    .concept-cta {
        margin-top: 3rem;
        text-align: center;
    }

    .concept-cta-link {
        display: inline-flex;
        align-items: center;
        gap: 0.45rem;
        padding: 0.85rem 1.7rem;
        border: 1px solid rgba(46, 111, 158, 0.35);
        border-radius: 999px;
        color: #2e6f9e;
        font-size: 0.98rem;
        font-weight: 600;
        text-decoration: none;
        transition:
            background 0.2s ease,
            gap 0.2s ease;
    }

    .concept-cta-link:hover {
        background: rgba(46, 111, 158, 0.08);
        gap: 0.65rem;
    }

    @media (min-width: 768px) {
        .services-section {
            padding: 4.5rem 2.5rem 0;
        }

        .services-grid {
            grid-template-columns: repeat(2, 1fr);
        }

        .concept-section {
            padding: 6rem 2.5rem;
        }

        .concept-grid {
            grid-template-columns: repeat(3, 1fr);
        }
    }

    @media (min-width: 1024px) {
        .concept-section {
            padding: 7rem 3rem;
        }
    }
</style>
